import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { CheckCircle2, ArrowRight, X } from 'lucide-react';
import { useScanProgress } from '../../contexts/ScanProgressContext';
import { Spinner } from '../../design-system';

/**
 * Repository-wide scan progress banner shown in the app shell.
 *
 * While a scan runs for the current repository it shows a lime "running" bar with the
 * live percentage; when the scan finishes it switches to a green "complete" state that
 * links to the findings and can be dismissed. Renders nothing when there is no active
 * or just-completed scan for the current repository.
 */
const ScanProgressBanner: React.FC = () => {
  const { state, batchId, scanTypeLabel, percent, findingsCount, applicationId, dismiss } =
    useScanProgress();
  const location = useLocation();

  // Dismiss the "complete" banner once the user reaches this repository's findings page
  // (via the link below or any other navigation) — it has served its purpose.
  useEffect(() => {
    if (
      state === 'complete' &&
      applicationId &&
      location.pathname.startsWith(`/repositories/${applicationId}/findings`)
    ) {
      dismiss();
    }
  }, [state, applicationId, location.pathname, dismiss]);

  if (!state || !applicationId) return null;

  const findingsLabel = findingsCount === 1 ? 'finding' : 'findings';

  if (state === 'running') {
    return (
      <div
        role="status"
        aria-live="polite"
        className="sticky top-0 z-20 bg-brand-primary/10 border-b border-brand-primary/20 px-4 py-2.5 shrink-0"
      >
        <div className="flex items-center gap-3 max-w-7xl mx-auto">
          <Spinner size={16} className="shrink-0" decorative />
          <span className="text-sm font-semibold text-brand-primary whitespace-nowrap">
            {scanTypeLabel} running
          </span>
          <div className="hidden sm:block h-1.5 w-full max-w-[280px] rounded-full bg-ui-background-tertiary overflow-hidden">
            <div
              className="h-full rounded-full bg-brand-primary transition-[width] duration-300"
              style={{ width: `${percent}%` }}
            />
          </div>
          <span className="hidden md:inline text-sm text-ui-text-secondary whitespace-nowrap">
            {findingsCount} {findingsLabel} so far
          </span>
        </div>
      </div>
    );
  }

  // state === 'complete'
  return (
    <div
      role="status"
      aria-live="polite"
      className="sticky top-0 z-20 bg-green-500/10 border-b border-green-500/30 px-4 py-2.5 shrink-0"
    >
      <div className="flex items-center gap-3 max-w-7xl mx-auto">
        <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
        <span className="text-sm font-semibold text-green-500 whitespace-nowrap">
          {scanTypeLabel} complete
        </span>
        <span className="hidden md:inline text-sm text-ui-text-secondary whitespace-nowrap">
          {findingsCount} actionable {findingsLabel}
        </span>
        <div className="flex-1" />
        <Link
          to={`/repositories/${applicationId}/findings?scan_batch_id=${batchId}`}
          onClick={dismiss}
          className="inline-flex items-center gap-1 text-sm font-semibold text-green-500 hover:underline whitespace-nowrap"
        >
          Review findings
          <ArrowRight className="w-4 h-4" />
        </Link>
        <button
          onClick={dismiss}
          aria-label="Dismiss"
          className="text-ui-text-tertiary hover:text-ui-text-primary transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default ScanProgressBanner;
